import React from 'react';
import { Lead } from '../../types/Lead';
import CompanyTags from '../ui/CompanyTags';
import MetricDisplay from '../ui/MetricDisplay';
import ScoreArc from '../ui/ScoreArc';
import { StarButton } from '../ui/StarButton';
import { TrelloButton } from '../ui/TrelloButton';
import { MemberManager } from '../members/MemberManager';

interface LeadCardHeaderProps {
  lead: Lead;
  isExpanded: boolean;
  onToggle: () => void;
  onMembersUpdate: () => void;
  memberRefreshTrigger?: number;
  isStarred?: boolean;
  onStarChange?: (isStarred: boolean) => void;
  customScore?: number;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
}

const LeadCardHeader: React.FC<LeadCardHeaderProps> = ({
  lead,
  isExpanded,
  onToggle,
  onMembersUpdate,
  memberRefreshTrigger,
  isStarred = false,
  onStarChange,
  customScore,
  onMemberChange
}) => {
  const topMetrics = Object.entries(lead.metrics)
    .sort(([,a], [,b]) => b.score - a.score)
    .slice(0, 3);

  return (
    <div
      style={{
        padding: '24px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '24px'
      }}
      onClick={onToggle}
    >
      <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            {lead.metadata.companyName}
          </h3>

          {/* Company Website Button */}
          {lead.metadata.companyWebsite && lead.metadata.companyWebsite.trim() && (
            <a
              href={lead.metadata.companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#dbeafe',
                color: '#1d4ed8',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                border: 'none',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                maxWidth: 'fit-content'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#bfdbfe';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Website
            </a>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {lead.id && (
              <StarButton
                leadId={lead.id}
                isStarred={isStarred}
                onStarChange={onStarChange}
                size="medium"
              />
            )}
            <TrelloButton
              lead={lead}
              size="small"
            />
          </div>
        </div>

        <CompanyTags metadata={lead.metadata} />

        {!isExpanded && (
          <>
            {/* Top Metrics Section */}
            <div style={{ marginTop: '18px' }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#6b7280',
                margin: '0 0 8px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Top Metrics
              </h4>
              <div style={{ display: 'flex', gap: '16px' }}>
                {topMetrics.map(([key, metric]) => (
                  <MetricDisplay
                    key={key}
                    metricKey={key}
                    score={metric.score}
                    compact={true}
                  />
                ))}
              </div>
            </div>

            {/* Team Members Section */}
            {lead.id && (
              <div style={{
                marginTop: '20px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  margin: '0 0 8px 0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Team Members
                </h4>
                <MemberManager
                  leadId={lead.id}
                  compact={true}
                  onMembersUpdate={onMembersUpdate}
                  memberRefreshTrigger={memberRefreshTrigger}
                  onMemberChange={onMemberChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <ScoreArc score={customScore ?? lead.processed.averageScore} />
        <svg
          width="24"
          height="24"
          fill="none"
          stroke="#6b7280"
          viewBox="0 0 24 24"
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default LeadCardHeader;